/**
 * QuizJustified — múltipla escolha com justificativa obrigatória.
 *
 * Slide do tipo mission com missionId='lab.assess.quizJustified'. Estende
 * o conceito do Quiz existente exigindo articulação do raciocínio em texto.
 *
 * Schema do frontmatter — ver SPEC-ATLAS-V2 §2.4.
 */

import { useState } from 'react'

import { useAdapter } from '../../adapter'
import type { MissionComponentProps } from '../../games/atlas/components'

interface QuestionOption {
  id: string
  text: string
}

interface Question {
  id: string
  prompt: string
  options: QuestionOption[]
  correctOptionId: string
  minJustificationLength?: number
}

interface QuestionAnswer {
  optionId: string
  justification: string
  submitted: boolean
}

export function QuizJustified(props: MissionComponentProps) {
  const adapter = useAdapter()
  const config = props.slideConfig ?? {}
  const slideIndex = props.slideIndex ?? 0
  const questions = ((config.questions as Question[]) || []).filter(
    (q) => q && q.id && Array.isArray(q.options),
  )

  const [answers, setAnswers] = useState<Record<string, QuestionAnswer>>({})

  function updateAnswer(qid: string, patch: Partial<QuestionAnswer>) {
    setAnswers((a) => ({
      ...a,
      [qid]: {
        optionId: a[qid]?.optionId ?? '',
        justification: a[qid]?.justification ?? '',
        submitted: a[qid]?.submitted ?? false,
        ...patch,
      },
    }))
  }

  function handleSubmit(q: Question) {
    const a = answers[q.id]
    if (!a || !a.optionId) return
    const minLen = q.minJustificationLength ?? 0
    if (a.justification.trim().length < minLen) return
    const isCorrect = a.optionId === q.correctOptionId
    adapter.submitQuizJustified({
      questionId: q.id,
      slideIndex,
      optionId: a.optionId,
      justification: a.justification,
      isCorrect,
    })
    updateAnswer(q.id, { submitted: true })
    if (props.onMissionEvent) {
      props.onMissionEvent('submitted', {
        questionId: q.id,
        optionId: a.optionId,
        isCorrect,
      })
    }
  }

  if (questions.length === 0) {
    return (
      <div style={{ padding: 24, color: 'var(--color-lab-rose, #D4474A)' }}>
        Sem perguntas configuradas para este quiz.
      </div>
    )
  }

  return (
    <div
      style={{
        maxWidth: 800,
        margin: '0 auto',
        padding: 'var(--spacing-lab-5, 24px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-lab-6, 32px)',
      }}
    >
      {questions.map((q, qi) => {
        const a = answers[q.id] ?? {
          optionId: '',
          justification: '',
          submitted: false,
        }
        const minLen = q.minJustificationLength ?? 0
        const canSubmit =
          a.optionId !== '' && a.justification.trim().length >= minLen
        return (
          <div
            key={q.id}
            style={{
              padding: 'var(--spacing-lab-4, 16px)',
              border: '1px solid var(--color-lab-rule, #D8D5CB)',
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 'var(--text-lab-lg, 1.25rem)' }}>
              {qi + 1}. {q.prompt}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {q.options.map((o) => {
                const checked = a.optionId === o.id
                const showCorrectness = a.submitted
                const isCorrect = o.id === q.correctOptionId
                let bg = checked ? 'var(--color-lab-bg-2, #F2F2ED)' : 'transparent'
                if (showCorrectness && isCorrect) bg = '#d4edda'
                else if (showCorrectness && checked && !isCorrect) bg = '#f8d7da'
                return (
                  <label
                    key={o.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      padding: 8,
                      cursor: a.submitted || props.readOnly ? 'default' : 'pointer',
                      background: bg,
                      borderRadius: 4,
                    }}
                  >
                    <input
                      type="radio"
                      name={q.id}
                      value={o.id}
                      checked={checked}
                      disabled={a.submitted || props.readOnly}
                      onChange={() => updateAnswer(q.id, { optionId: o.id })}
                      style={{ marginTop: 3 }}
                    />
                    <span>{o.text}</span>
                  </label>
                )
              })}
            </div>
            <div>
              <label
                style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}
              >
                Por quê?{' '}
                {minLen > 0 && (
                  <span style={{ opacity: 0.6, fontWeight: 400, fontSize: '0.85em' }}>
                    (mín. {minLen} chars)
                  </span>
                )}
              </label>
              <textarea
                value={a.justification}
                onChange={(e) => updateAnswer(q.id, { justification: e.target.value })}
                disabled={a.submitted || props.readOnly}
                rows={3}
                style={{
                  width: '100%',
                  padding: 10,
                  fontFamily: 'inherit',
                  fontSize: 'var(--text-lab-base, 1rem)',
                  border: '1px solid var(--color-lab-rule, #D8D5CB)',
                  borderRadius: 6,
                  resize: 'vertical',
                }}
              />
            </div>
            <button
              onClick={() => handleSubmit(q)}
              disabled={!canSubmit || a.submitted || props.readOnly}
              style={{
                padding: '8px 24px',
                background: a.submitted
                  ? 'var(--color-lab-accent, #6aa)'
                  : 'var(--color-lab-accent, #5B2DB8)',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: canSubmit && !a.submitted ? 'pointer' : 'default',
                opacity: canSubmit || a.submitted ? 1 : 0.5,
                alignSelf: 'flex-end',
                fontSize: 'var(--text-lab-base, 1rem)',
              }}
            >
              {a.submitted ? 'Enviado' : 'Enviar'}
            </button>
          </div>
        )
      })}
    </div>
  )
}
